import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

// Sales-team internal signup only ever pre-processes one of these 4
// packages -- fixed order, fixed set, labeled by price rather than the
// package's normal marketing name.
const PREPROCESS_CODES = ['0550', '0880', '4500', '38000'];

export default function SignupInternal() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const allowed = ['sales-team', 'admin', 'superadmin'].includes(user?.role);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [wellisId, setWellisId] = useState('');
  const [wellisIdAvailable, setWellisIdAvailable] = useState(null);
  const [wellisIdError, setWellisIdError] = useState('');
  const [checkingWellisId, setCheckingWellisId] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packageId, setPackageId] = useState('');
  const [creditBalance, setCreditBalance] = useState(0);
  const [rnrBalance, setRnrBalance] = useState(0);
  const [rnrHistory, setRnrHistory] = useState([]);
  const [conversionRate, setConversionRate] = useState(1);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  // Outstanding signups + cash remittance
  const [outstandingOrders, setOutstandingOrders] = useState([]);
  const [loadingOutstanding, setLoadingOutstanding] = useState(false);
  const [remitAmounts, setRemitAmounts] = useState({});
  const [remitNotes, setRemitNotes] = useState({});
  const [submittingRemitFor, setSubmittingRemitFor] = useState(null);
  const [remitMessage, setRemitMessage] = useState('');
  const [remitError, setRemitError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!allowed) {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate, allowed]);

  // Pre-fill the sponsor field with the operator's own Wellis ID -- still
  // freely editable, just a sensible default for the common case.
  useEffect(() => {
    if (user?.wellis_id) {
      setReferralCode((prev) => prev || user.wellis_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.wellis_id]);

  const loadData = useCallback(async () => {
    try {
      const [packagesRes, balanceRes, historyRes, rateRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/packages`),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-balance`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-transactions?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/rnr-conversion-rate`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (packagesRes.ok) {
        const data = await packagesRes.json();
        const all = data.packages || [];
        const preprocessOnly = PREPROCESS_CODES
          .map((code) => all.find((p) => p.code === code))
          .filter(Boolean);
        setPackages(preprocessOnly);
      }
      if (balanceRes.ok) {
        const data = await balanceRes.json();
        setCreditBalance(data.credit_point_balance || 0);
        setRnrBalance(data.rnr_point_balance || 0);
      }
      if (historyRes.ok) {
        const data = await historyRes.json();
        setRnrHistory((data.transactions || []).filter((t) => t.point_type === 'RNR'));
      }
      if (rateRes.ok) {
        const data = await rateRes.json();
        setConversionRate(data.rate || 1);
      }
    } catch (err) {
      console.error('[SignupInternal] loadData error:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!user || !allowed) return;
    loadData();
  }, [user, allowed, loadData]);

  const checkWellisIdUniqueness = async () => {
    if (!wellisId || wellisId.length !== 10) {
      setWellisIdError(tt('Wellis ID must be exactly 10 alphanumeric characters', 'Wellis ID 必须恰好为 10 个字母数字'));
      return;
    }
    setCheckingWellisId(true);
    setWellisIdError('');
    setWellisIdAvailable(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/check-wellis-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wellis_id: `WELLIS-${wellisId.toUpperCase()}` }),
      });
      const data = await res.json();
      if (!res.ok) {
        setWellisIdError(data.error || tt('Failed to check availability', '无法检查可用性'));
        setWellisIdAvailable(false);
      } else if (data.available) {
        setWellisIdAvailable(true);
      } else {
        setWellisIdAvailable(false);
        setWellisIdError(tt('This Wellis ID is already taken', '此 Wellis ID 已被占用'));
      }
    } catch (err) {
      setWellisIdError(err.message || tt('Failed to check availability', '无法检查可用性'));
      setWellisIdAvailable(false);
    } finally {
      setCheckingWellisId(false);
    }
  };

  const fetchOutstanding = useCallback(async () => {
    if (user?.role !== 'sales-team') return;
    setLoadingOutstanding(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/sales-team-outstanding`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setOutstandingOrders(data.orders || []);
      }
    } catch (err) {
      console.error('[SignupInternal] fetchOutstanding error:', err);
    } finally {
      setLoadingOutstanding(false);
    }
  }, [token, user?.role]);

  useEffect(() => {
    if (!user || !allowed) return;
    fetchOutstanding();
  }, [user, allowed, fetchOutstanding]);

  const handleSubmitRemittance = async (orderId) => {
    setRemitMessage('');
    setRemitError('');
    const amount = parseFloat(remitAmounts[orderId]);
    if (!amount || amount <= 0) {
      setRemitError(tt('Enter a valid amount', '请输入有效金额'));
      return;
    }
    setSubmittingRemitFor(orderId);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/submit-remittance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          package_order_id: orderId,
          amount_cp: amount,
          notes: remitNotes[orderId]?.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to submit remittance', '提交汇款失败'));
      }
      setRemitMessage(tt('Remittance submitted, pending admin review.', '汇款已提交，等待管理员审核。'));
      setRemitAmounts((prev) => ({ ...prev, [orderId]: '' }));
      setRemitNotes((prev) => ({ ...prev, [orderId]: '' }));
      await fetchOutstanding();
    } catch (err) {
      setRemitError(err.message || tt('Failed to submit remittance', '提交汇款失败'));
    } finally {
      setSubmittingRemitFor(null);
    }
  };

  const selectedPackage = packages.find((p) => p.id === packageId);
  const price = selectedPackage ? Number(selectedPackage.price) : 0;
  const shortfall = Math.max(price - creditBalance, 0);
  const rnrNeeded = shortfall > 0 && conversionRate > 0 ? Math.ceil((shortfall / conversionRate) * 100) / 100 : 0;
  const willAutoConvertRnr = shortfall > 0 && rnrNeeded <= rnrBalance;
  const balanceAfter = creditBalance - price + (willAutoConvertRnr ? rnrNeeded * conversionRate : 0);
  const combinedSpendingPower = creditBalance + rnrBalance * conversionRate;
  const insufficientBalance = !!selectedPackage && combinedSpendingPower < price;

  const canSubmit =
    firstName.trim() &&
    lastName.trim() &&
    (email.trim() || phone.trim()) &&
    password.length >= 8 &&
    confirmPassword === password &&
    referralCode.trim() &&
    wellisId.length === 10 &&
    wellisIdAvailable === true &&
    packageId &&
    !insufficientBalance &&
    !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError('');
    setSuccess(null);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/signup-internal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          password,
          referral_code: referralCode.trim(),
          wellis_id: wellisId.trim(),
          package_id: packageId,
          notes: notes.trim() || null,
        }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to create member', '创建会员失败'));
      }

      setSuccess({ wellisId: data.new_user_affiliate_code });
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setPassword('');
      setConfirmPassword('');
      setReferralCode('');
      setWellisId('');
      setWellisIdAvailable(null);
      setWellisIdError('');
      setPackageId('');
      setNotes('');
      await loadData();
      await fetchOutstanding();
    } catch (err) {
      setError(err.message || tt('Failed to create member', '创建会员失败'));
    } finally {
      setLoading(false);
    }
  };

  if (!user || !allowed) {
    return null;
  }

  return (
    <div className="min-h-screen p-margin-mobile pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-container-max mx-auto" style={{ maxWidth: '640px' }}>
        <div className="mb-8 border-b pb-6" style={{ borderColor: '#c9a961' }}>
          <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>
            {tt('Internal Signup', '内部注册')}
          </h1>
          <p className="text-body-md" style={{ color: '#b8a68f' }}>
            {tt(
              'Instantly sign up and activate a package for a new member, funded from your own Credit Points balance.',
              '即时为新会员注册并激活套餐，费用从您自己的认购积分余额中扣除。'
            )}
          </p>
        </div>

        <div className="border rounded-lg p-6 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <div className="flex justify-between items-center">
            <span style={{ color: '#b8a68f' }}>{tt('Your RnR Points balance', '您的 RnR 积分余额')}</span>
            <span className="text-headline-md font-bold" style={{ color: '#c9a961' }}>{rnrBalance.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span style={{ color: '#b8a68f' }}>{tt('Your Credit Points balance', '您的认购积分余额')}</span>
            <span className="text-headline-md font-bold" style={{ color: '#c9a961' }}>{creditBalance.toFixed(2)}</span>
          </div>
          {selectedPackage && willAutoConvertRnr && (
            <p className="text-body-xs mt-2" style={{ color: '#c9a961' }}>
              {tt(
                `Your Credit Points balance is not enough on its own -- ${rnrNeeded.toFixed(2)} RnR Points (rate: ${conversionRate}) will be automatically converted to cover the shortfall.`,
                `您的认购积分余额不足——将自动转换 ${rnrNeeded.toFixed(2)} RnR 积分（汇率：${conversionRate}）以补足差额。`
              )}
            </p>
          )}
          {selectedPackage && (
            <div className="flex justify-between items-center mt-2">
              <span style={{ color: '#b8a68f' }}>{tt('Balance after this signup', '注册后的余额')}</span>
              <span className="text-headline-md font-bold" style={{ color: insufficientBalance ? '#e05252' : '#c9a961' }}>
                {balanceAfter.toFixed(2)}
              </span>
            </div>
          )}
          {insufficientBalance && (
            <p className="text-body-xs mt-2" style={{ color: '#e05252' }}>
              {tt(
                'Insufficient combined balance for this package -- your Credit Points plus RnR Points (converted at the current rate) don\'t cover the price.',
                '组合余额不足以支付此套餐——您的认购积分加上按当前汇率转换的 RnR 积分不足以支付此价格。'
              )}
            </p>
          )}
        </div>

        <div className="border rounded-lg p-6 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <p className="text-body-sm font-medium mb-3" style={{ color: '#c9a961' }}>
            {tt('RnR Points usage history', 'RnR 积分使用记录')}
          </p>
          {rnrHistory.length === 0 ? (
            <p className="text-body-xs" style={{ color: '#b8a68f' }}>
              {tt('No RnR Points activity yet.', '暂无 RnR 积分记录。')}
            </p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {rnrHistory.map((t) => (
                <div key={t.id} className="flex justify-between items-center text-body-xs border-b pb-2" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                  <div>
                    <p style={{ color: '#b8a68f' }}>{t.notes || tt('RnR transaction', 'RnR 交易')}</p>
                    <p style={{ color: '#999999' }}>{new Date(t.created_at).toLocaleString()}</p>
                  </div>
                  <span className="font-bold" style={{ color: Number(t.amount) >= 0 ? '#c9a961' : '#e05252' }}>
                    {Number(t.amount) >= 0 ? '+' : ''}{Number(t.amount).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {user?.role === 'sales-team' && (
          <div className="border rounded-lg p-6 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <p className="text-body-sm font-medium mb-1" style={{ color: '#c9a961' }}>
              {tt('My Outstanding Signups', '我的未结清注册')}
            </p>
            <p className="text-body-xs mb-3" style={{ color: '#b8a68f' }}>
              {tt(
                'Signups you funded that still need the collected cash sent in.',
                '您资助的注册中，仍需汇入所收现金的记录。'
              )}
            </p>
            {remitMessage && <p className="text-body-sm mb-3" style={{ color: '#c9a961' }}>{remitMessage}</p>}
            {remitError && <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{remitError}</p>}
            {loadingOutstanding ? (
              <p className="text-body-xs" style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
            ) : outstandingOrders.length === 0 ? (
              <p className="text-body-xs" style={{ color: '#b8a68f' }}>{tt('Nothing outstanding.', '暂无未结清项目。')}</p>
            ) : (
              <div className="space-y-3">
                {outstandingOrders.map((o) => (
                  <div key={o.package_order_id} className="border rounded p-3" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
                    <div className="flex justify-between items-start text-body-xs mb-2">
                      <div>
                        <p style={{ color: '#c9a961' }}>{o.member ? `${o.member.first_name || ''} ${o.member.last_name || ''} (${o.member.affiliate_code || '—'})` : '—'}</p>
                        <p style={{ color: '#b8a68f' }}>{o.package_code} · {tt('Outstanding', '未结')}: {Number(o.outstanding).toFixed(2)} CP</p>
                        {o.pending_remitted > 0 && (
                          <p style={{ color: '#999999' }}>{tt('Pending review', '待审核')}: {Number(o.pending_remitted).toFixed(2)} CP</p>
                        )}
                      </div>
                      <span style={{ color: o.days_outstanding > 10 ? '#e05252' : o.days_outstanding >= 6 ? '#d97706' : '#b8a68f' }}>
                        {o.days_outstanding} {tt('days', '天')}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={o.claimable}
                        placeholder={tt('Amount', '金额')}
                        value={remitAmounts[o.package_order_id] || ''}
                        onChange={(e) => setRemitAmounts((prev) => ({ ...prev, [o.package_order_id]: e.target.value }))}
                        className="w-24 p-2 rounded border text-body-xs"
                        style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                      />
                      <input
                        type="text"
                        placeholder={tt('Notes (optional)', '备注（可选）')}
                        value={remitNotes[o.package_order_id] || ''}
                        onChange={(e) => setRemitNotes((prev) => ({ ...prev, [o.package_order_id]: e.target.value }))}
                        className="flex-1 p-2 rounded border text-body-xs"
                        style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                      />
                      <button
                        type="button"
                        onClick={() => handleSubmitRemittance(o.package_order_id)}
                        disabled={submittingRemitFor === o.package_order_id || o.claimable <= 0}
                        className="px-3 py-2 rounded font-medium text-body-xs disabled:opacity-50 whitespace-nowrap"
                        style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                      >
                        {submittingRemitFor === o.package_order_id ? tt('Submitting...', '提交中...') : tt('Send In Cash Points', '汇入现金积分')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {success ? (
          <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <p className="text-headline-sm font-bold mb-2" style={{ color: '#c9a961' }}>
              {tt('Member created successfully', '会员创建成功')}
            </p>
            <p style={{ color: '#b8a68f' }}>
              {tt('Wellis ID', 'Wellis ID')}: <span className="font-mono font-bold" style={{ color: '#c9a961' }}>{success.wellisId}</span>
            </p>
            <button
              onClick={() => setSuccess(null)}
              className="w-full mt-4 px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
              style={{ backgroundColor: '#c9a961' }}
            >
              {tt('Sign up another member', '注册另一位会员')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="border rounded-lg p-6 space-y-4" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            {error && (
              <div className="p-3 rounded border text-body-sm" style={{ backgroundColor: 'rgba(224,82,82,0.1)', borderColor: '#e05252', color: '#e05252' }}>
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('First Name', '名字')}</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full p-3 rounded border"
                  style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                />
              </div>
              <div>
                <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Last Name', '姓氏')}</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full p-3 rounded border"
                  style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                />
              </div>
            </div>

            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Email', '电子邮件')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              />
            </div>

            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Phone (optional if email provided)', '电话（如已提供电子邮件则可选）')}</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              />
            </div>

            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Password', '密码')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              />
              <p className="text-body-xs mt-1" style={{ color: '#b8a68f' }}>
                {tt('Required: at least 8 characters. Recommended: a mix of uppercase, lowercase, numbers and symbols.', '要求：至少 8 个字符。建议：包含大写字母、小写字母、数字和符号。')}
              </p>
            </div>

            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Confirm Password', '确认密码')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: confirmPassword && confirmPassword !== password ? '#e05252' : '#c9a961', color: '#b8a68f' }}
              />
              {confirmPassword && confirmPassword !== password && (
                <p className="text-body-xs mt-1" style={{ color: '#e05252' }}>
                  {tt('Passwords do not match', '密码不匹配')}
                </p>
              )}
            </div>

            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Sponsor Referral Code', '推荐人代码')}</label>
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              />
            </div>

            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('New Member\'s Wellis ID', '新会员的 Wellis ID')}</label>
              <div className="flex gap-2">
                <div className="flex-1 flex items-center rounded border overflow-hidden" style={{ backgroundColor: '#152214', borderColor: '#c9a961' }}>
                  <span className="pl-3 text-body-sm" style={{ color: '#999999' }}>WELLIS-</span>
                  <input
                    type="text"
                    value={wellisId}
                    maxLength={10}
                    onChange={(e) => {
                      setWellisId(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
                      setWellisIdAvailable(null);
                      setWellisIdError('');
                    }}
                    className="w-full p-3 bg-transparent focus:outline-none"
                    style={{ color: '#b8a68f' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={checkWellisIdUniqueness}
                  disabled={checkingWellisId || wellisId.length !== 10}
                  className="px-4 py-2 rounded-lg font-medium disabled:opacity-50 whitespace-nowrap"
                  style={{ backgroundColor: '#3d5a4a', color: '#c9a961' }}
                >
                  {checkingWellisId ? tt('Checking...', '检查中...') : tt('Check', '检查')}
                </button>
              </div>
              <p className="text-body-xs mt-1" style={{ color: '#b8a68f' }}>
                {tt('10 alphanumeric characters', '10 个字母数字字符')}
              </p>
              {wellisIdAvailable === true && (
                <p className="text-body-xs mt-1" style={{ color: '#c9a961' }}>✓ {tt('Available', '可用')}</p>
              )}
              {wellisIdError && (
                <p className="text-body-xs mt-1" style={{ color: '#e05252' }}>{wellisIdError}</p>
              )}
            </div>

            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Package', '套餐')}</label>
              <select
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              >
                <option value="">{tt('Select a package...', '选择套餐...')}</option>
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {tt(
                      `PreProcess ${Number(p.price).toFixed(0)} Credit Points`,
                      `预处理 ${Number(p.price).toFixed(0)} 认购积分`
                    )}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Notes (optional)', '备注（可选）')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="2"
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 text-black"
              style={{ backgroundColor: '#c9a961' }}
            >
              {loading ? tt('Creating...', '创建中...') : tt('Create Member & Activate Package', '创建会员并激活套餐')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
